/**
 * FlowCanvas + NodeConfigPanel + BranchEditor + FlowNode renderer.
 * Extracted from FlowsPage.jsx (iter83 refactor).
 */
import { useEffect, useState, useCallback } from "react";
import {
  ReactFlow, Background, Controls, MiniMap, addEdge,
  useNodesState, useEdgesState, Handle, Position, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Zap, Filter, Bell, Play, Clock, GitBranch, Trash2, Plus, Save,
  ChevronLeft, FlaskConical, ToggleRight, ToggleLeft, X, HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import FlowTutorialDialog from "@/components/FlowTutorialDialog";
import { API, auth } from "./shared";
import { ChipInput } from "./NewFlowWizard";

const NODE_STYLE = {
  TRIGGER:   { bg: "#E6F1FB", border: "#3B82F6", icon: Zap,       label: "Trigger" },
  CONDITION: { bg: "#FAEEDA", border: "#F59E0B", icon: Filter,    label: "Condition" },
  ACTION:    { bg: "#EAF3DE", border: "#22C55E", icon: Play,      label: "Action" },
  NOTIFY:    { bg: "#FAEDED", border: "#8B1A1A", icon: Bell,      label: "Notify" },
  WAIT:      { bg: "#F1EFE8", border: "#6B7280", icon: Clock,     label: "Wait" },
  BRANCH:    { bg: "#EEEDFE", border: "#8B5CF6", icon: GitBranch, label: "Branch" },
};


function FlowNode({ data, selected }) {
  const s = NODE_STYLE[data.type] || NODE_STYLE.ACTION;
  const Icon = s.icon;
  const branchLabels = data.type === "BRANCH" ? ((data.config?.branches || []).map((b) => b.label).filter(Boolean)) : [];
  return (
    <div
      className="rounded-lg shadow-sm transition-all"
      style={{
        background: s.bg, border: `2px solid ${selected ? "#000" : s.border}`,
        padding: "10px 14px", minWidth: 180, fontFamily: "Inter, sans-serif",
      }}
      data-testid={`flow-node-${data.type}`}
    >
      {data.type !== "TRIGGER" && <Handle type="target" position={Position.Left} />}
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: s.border }}>
        <Icon size={14} />
        <span>{s.label}</span>
      </div>
      <div className="mt-1 text-sm font-medium text-slate-900 truncate" style={{ maxWidth: 200 }}>
        {data.label || data.title || s.label}
      </div>
      {data.type === "CONDITION" ? (
        <>
          <Handle type="source" position={Position.Right} id="YES" style={{ top: "35%", background: "#22C55E" }} />
          <Handle type="source" position={Position.Right} id="NO" style={{ top: "70%", background: "#EF4444" }} />
        </>
      ) : data.type === "BRANCH" && branchLabels.length > 0 ? (
        <>
          {branchLabels.map((label, i) => {
            const top = 30 + (i * 60) / Math.max(branchLabels.length, 1);
            return (
              <div key={label} style={{ position: "absolute", right: -4, top: `${top}%`, transform: "translate(100%, -50%)" }}>
                <span className="text-[9px] text-pink-700 bg-white/80 px-1 rounded">{label}</span>
                <Handle type="source" position={Position.Right} id={label} style={{ top: 0, background: "#EC4899" }} />
              </div>
            );
          })}
        </>
      ) : (
        <Handle type="source" position={Position.Right} />
      )}
    </div>
  );
}

const nodeTypes = { flow: FlowNode };

const palette = [
  { type: "TRIGGER", title: "Trigger" },
  { type: "CONDITION", title: "Condition" },
  { type: "ACTION", title: "Action" },
  { type: "NOTIFY", title: "Notify" },
  { type: "WAIT", title: "Wait" },
  { type: "BRANCH", title: "Branch" },
];

function fromFlowDoc(flow) {
  const nodes = (flow.nodes || []).map((n) => ({
    id: n.id,
    type: "flow",
    position: n.position || { x: 100, y: 100 },
    data: { type: n.type, label: n.config?.label || n.config?.title || n.config?.event || n.config?.action, config: n.config || {} },
  }));
  const edges = (flow.edges || []).map((e, i) => ({
    id: `e_${i}_${e.from_node_id}_${e.to_node_id}_${e.condition_branch || ""}`,
    source: e.from_node_id,
    target: e.to_node_id,
    sourceHandle: e.condition_branch || null,
    label: e.condition_branch || "",
    markerEnd: { type: MarkerType.ArrowClosed },
    style: e.condition_branch === "NO" ? { stroke: "#EF4444" } : e.condition_branch === "YES" ? { stroke: "#22C55E" } : {},
  }));
  return { nodes, edges };
}

function toFlowDoc(nodes, edges) {
  return {
    nodes: nodes.map((n) => ({
      id: n.id,
      type: n.data.type,
      position: n.position,
      config: n.data.config || {},
    })),
    edges: edges.map((e) => ({
      from_node_id: e.source,
      to_node_id: e.target,
      condition_branch: e.sourceHandle || null,
    })),
  };
}

function FlowCanvas({ flowId, onBack }) {
  const [flow, setFlow] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selected, setSelected] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [variables, setVariables] = useState([]);
  const [actions, setActions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/${flowId}`, { headers: auth() }).then(r => r.json()).then(j => {
      const f = j.data; if (!f) return;
      setFlow(f);
      const { nodes, edges } = fromFlowDoc(f);
      setNodes(nodes); setEdges(edges);
    });
    fetch(`${API}/triggers`, { headers: auth() }).then(r => r.json()).then(j => setTriggers(j.data || []));
    fetch(`${API}/actions`, { headers: auth() }).then(r => r.json()).then(j => setActions(j.data || []));
    fetch(`${API}/${flowId}/runs?limit=20`, { headers: auth() }).then(r => r.json()).then(j => setRuns(j.data || []));
  }, [flowId, setNodes, setEdges]);

  useEffect(() => {
    if (flow?.trigger?.event) {
      fetch(`${API}/variables?event=${flow.trigger.event}`, { headers: auth() })
        .then(r => r.json()).then(j => setVariables(j.data || []));
    }
  }, [flow?.trigger?.event]);

  const onConnect = useCallback((conn) => {
    const newEdge = {
      ...conn,
      id: `e_${Date.now()}`,
      markerEnd: { type: MarkerType.ArrowClosed },
      sourceHandle: conn.sourceHandle || null,
      style: conn.sourceHandle === "NO" ? { stroke: "#EF4444" } : conn.sourceHandle === "YES" ? { stroke: "#22C55E" } : {},
      label: conn.sourceHandle || "",
    };
    setEdges((eds) => addEdge(newEdge, eds));
  }, [setEdges]);

  const addNode = (type) => {
    const id = `n_${Date.now()}`;
    const x = 200 + Math.random() * 200;
    const y = 200 + Math.random() * 100;
    setNodes((ns) => [...ns, { id, type: "flow", position: { x, y }, data: { type, config: {}, label: type } }]);
  };

  const updateSelected = (patch) => {
    if (!selected) return;
    setNodes((ns) => ns.map((n) => n.id === selected.id ? { ...n, data: { ...n.data, ...patch, config: { ...(n.data.config || {}), ...(patch.config || {}) } } } : n));
    setSelected((s) => s ? { ...s, data: { ...s.data, ...patch, config: { ...(s.data.config || {}), ...(patch.config || {}) } } } : s);
  };

  const save = async () => {
    if (!flow) return;
    const payload = { ...flow, ...toFlowDoc(nodes, edges) };
    try {
      const r = await fetch(`${API}/${flow.flow_id}`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.error?.message || j.detail || `Save failed (${r.status})`);
        return;
      }
      const j = await r.json();
      if (j.success && j.data) {
        // Refresh state from server so the next save isn't built on stale data.
        setFlow(j.data);
        const { nodes: nn, edges: ee } = fromFlowDoc(j.data);
        setNodes(nn);
        setEdges(ee);
        toast.success("Flow saved");
      } else {
        toast.error(j.error?.message || "Save failed (unexpected response)");
      }
    } catch (e) {
      toast.error(`Save failed: ${e.message || "network error"}`);
    }
  };

  const testRun = async () => {
    const sample = Object.fromEntries(variables.map(v => [v, v === "amount" ? 75000 : v.includes("count") ? 3 : `<${v}>`]));
    const r = await fetch(`${API}/${flow.flow_id}/test`, {
      method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ trigger_data: sample }),
    });
    const j = await r.json();
    if (j.success) {
      toast.success(`Test run completed: ${j.data?.steps?.length || 0} steps`);
      fetch(`${API}/${flow.flow_id}/runs?limit=20`, { headers: auth() }).then(r => r.json()).then(j2 => setRuns(j2.data || []));
    } else toast.error("Test run failed");
  };

  if (!flow) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="h-screen flex flex-col" data-testid="flow-canvas-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-white dark:bg-slate-900">
        <Button variant="ghost" size="sm" onClick={onBack}><ChevronLeft size={16} /> Back</Button>
        <Input value={flow.name} onChange={(e) => setFlow({ ...flow, name: e.target.value })}
               className="text-lg font-semibold border-0 focus-visible:ring-0 flex-1" data-testid="flow-name-input" />
        <Badge variant={flow.is_active ? "default" : "outline"}>{flow.is_active ? "Active" : "Paused"}</Badge>
        <Button variant="outline" size="sm" onClick={() => setTutorialOpen(true)} data-testid="canvas-tutorial-btn" title="How does Flow Builder work?">
          <HelpCircle size={14} className="mr-1" /> Tutorial
        </Button>
        <Button variant="outline" size="sm" onClick={testRun} data-testid="test-run-btn"><FlaskConical size={14} className="mr-1" /> Test run</Button>
        <Button size="sm" onClick={save} data-testid="save-flow-btn" className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white"><Save size={14} className="mr-1" /> Save</Button>
      </div>
      <FlowTutorialDialog open={tutorialOpen} onClose={() => setTutorialOpen(false)} />

      <div className="flex flex-1 overflow-hidden">
        {/* Left palette */}
        <div className="w-52 border-r bg-slate-50 dark:bg-slate-800 p-3 overflow-y-auto">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Add node</div>
          {palette.map((p) => {
            const s = NODE_STYLE[p.type]; const Icon = s.icon;
            return (
              <button key={p.type}
                      onClick={() => addNode(p.type)}
                      data-testid={`add-${p.type.toLowerCase()}-btn`}
                      className="w-full mb-2 p-2.5 rounded-md border-2 text-left text-sm font-semibold hover:shadow-md hover:-translate-y-px transition"
                      style={{ background: s.bg, borderColor: s.border, color: s.border }}>
                <Icon size={14} className="inline mr-2" /> {s.label}
              </button>
            );
          })}

          <div className="mt-6 text-xs font-semibold text-slate-500 uppercase mb-2">Trigger</div>
          <Select value={flow.trigger?.event || ""} onValueChange={(v) => setFlow({ ...flow, trigger: { ...(flow.trigger || {}), event: v } })}>
            <SelectTrigger data-testid="trigger-select"><SelectValue placeholder="Select event" /></SelectTrigger>
            <SelectContent>
              {triggers.map(t => <SelectItem key={t.event} value={t.event}>{t.event}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="mt-6 text-xs font-semibold text-slate-500 uppercase mb-2">Recent runs</div>
          <div className="space-y-1 text-xs">
            {runs.slice(0, 8).map(r => (
              <div key={r.run_id} className="p-1.5 rounded border bg-white dark:bg-slate-900 dark:border-slate-700">
                <div className="font-mono text-[10px]">{r.run_id.slice(0, 12)}</div>
                <div className={r.status === "COMPLETED" ? "text-green-600" : r.status === "FAILED" ? "text-red-600" : "text-amber-600"}>{r.status}</div>
                <div className="text-slate-400">{r.steps?.length || 0} steps</div>
              </div>
            ))}
            {runs.length === 0 && <div className="text-slate-400">No runs yet</div>}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative" data-testid="flow-canvas">
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onConnect={onConnect} onNodeClick={(_, n) => setSelected(n)} onPaneClick={() => setSelected(null)}
            nodeTypes={nodeTypes} fitView
          >
            <Background />
            <Controls />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </div>

        {/* Right panel */}
        {selected && (
          <div className="w-80 border-l bg-white dark:bg-slate-900 p-4 overflow-y-auto" data-testid="node-config-panel">
            <NodeConfigPanel
              node={selected}
              variables={variables}
              actions={actions}
              triggers={triggers}
              onChange={updateSelected}
              onDelete={() => { setNodes((ns) => ns.filter((n) => n.id !== selected.id)); setEdges((es) => es.filter((e) => e.source !== selected.id && e.target !== selected.id)); setSelected(null); }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function NodeConfigPanel({ node, variables, actions, triggers, onChange, onDelete }) {
  const type = node.data.type;
  const cfg = node.data.config || {};

  const setCfg = (patch) => onChange({ config: { ...cfg, ...patch } });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase text-slate-500">{NODE_STYLE[type]?.label}</div>
        <button onClick={onDelete} className="text-red-500 hover:text-red-700" data-testid="delete-node-btn"><Trash2 size={16} /></button>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Label</label>
        <Input value={node.data.label || ""} onChange={(e) => onChange({ label: e.target.value })} data-testid="node-label-input" />
      </div>

      {type === "TRIGGER" && (
        <>
          <div>
            <label className="text-xs font-medium">Event</label>
            <Select value={cfg.event || ""} onValueChange={(v) => setCfg({ event: v })}>
              <SelectTrigger><SelectValue placeholder="Pick event" /></SelectTrigger>
              <SelectContent>{triggers.map(t => <SelectItem key={t.event} value={t.event}>{t.event}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </>
      )}

      {type === "CONDITION" && (
        <>
          <Input placeholder="Field (e.g. amount)" value={cfg.field || ""} onChange={(e) => setCfg({ field: e.target.value })} data-testid="cond-field-input" />
          <Select value={cfg.operator || "GT"} onValueChange={(v) => setCfg({ operator: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["EQ", "NEQ", "GT", "LT", "GTE", "LTE", "CONTAINS", "IS_NULL", "IS_NOT_NULL"].map(o => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder="Value" value={cfg.value ?? ""} onChange={(e) => setCfg({ value: isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value) })} data-testid="cond-value-input" />
        </>
      )}

      {type === "NOTIFY" && (
        <>
          <div>
            <label className="text-xs font-medium">Channels</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {["PUSH", "IN_APP", "SMS", "WHATSAPP", "EMAIL"].map(ch => {
                const has = (cfg.channels || ["PUSH", "IN_APP"]).includes(ch);
                return (
                  <button key={ch} onClick={() => {
                    const cur = new Set(cfg.channels || ["PUSH", "IN_APP"]);
                    has ? cur.delete(ch) : cur.add(ch);
                    setCfg({ channels: Array.from(cur) });
                  }}
                  className={`px-2 py-0.5 rounded text-xs border ${has ? "bg-[#8B1A1A] text-white border-[#8B1A1A]" : "bg-white text-slate-600"}`}
                  data-testid={`channel-${ch}`}>{ch}</button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium">Recipients</label>
            <Select value={cfg.recipients?.type || "ROLE"} onValueChange={(v) => setCfg({ recipients: { type: v, value: "" } })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["USER", "ROLE", "REGION", "ADMIN", "MANAGER"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Value (e.g. SALES_ADMIN or {{salesman_id}})" value={cfg.recipients?.value || ""}
                   onChange={(e) => setCfg({ recipients: { ...(cfg.recipients || {}), value: e.target.value } })} className="mt-1" data-testid="recipient-value-input" />
          </div>
          <Input placeholder="Title (e.g. New order: ₹{{amount}})" value={cfg.title || ""} onChange={(e) => setCfg({ title: e.target.value })} data-testid="notify-title-input" />
          <Textarea placeholder="Body — use {{variable}} for templates" value={cfg.body || ""} onChange={(e) => setCfg({ body: e.target.value })} rows={3} data-testid="notify-body-input" />
          <Input placeholder="Deep link type (e.g. ORDER_STATUS)" value={cfg.deep_link_type || ""} onChange={(e) => setCfg({ deep_link_type: e.target.value })} />
          {variables.length > 0 && (
            <div className="text-xs text-slate-500">
              <div className="font-semibold mb-1">Available vars</div>
              <div className="flex flex-wrap gap-1">
                {variables.map(v => <code key={v} className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{`{{${v}}}`}</code>)}
              </div>
            </div>
          )}
        </>
      )}

      {type === "ACTION" && (
        <>
          <Select value={cfg.action || ""} onValueChange={(v) => setCfg({ action: v })}>
            <SelectTrigger><SelectValue placeholder="Pick action" /></SelectTrigger>
            <SelectContent>
              {actions.map(a => <SelectItem key={a.action} value={a.action}>{a.action}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea placeholder='Params JSON (e.g. {"status":"APPROVED"})' value={JSON.stringify(cfg.params || {})} onChange={(e) => { try { setCfg({ params: JSON.parse(e.target.value) }); } catch (_) { } }} rows={4} data-testid="action-params-input" />
        </>
      )}

      {type === "WAIT" && (
        <>
          <Input type="number" placeholder="Duration" value={cfg.duration ?? ""} onChange={(e) => setCfg({ duration: Number(e.target.value) })} data-testid="wait-duration-input" />
          <Select value={cfg.unit || "MINUTES"} onValueChange={(v) => setCfg({ unit: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["SECONDS", "MINUTES", "HOURS", "DAYS"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </>
      )}

      {type === "BRANCH" && (
        <BranchEditor branches={cfg.branches || []} onChange={(branches) => setCfg({ branches })} />
      )}
    </div>
  );
}

// ───────────── Branch editor ─────────────

function BranchEditor({ branches, onChange }) {
  const update = (i, patch) => {
    const next = branches.map((b, j) => (j === i ? { ...b, ...patch } : b));
    onChange(next);
  };
  const updateCond = (i, patch) => {
    const b = branches[i] || {};
    const cond = { ...(b.condition || {}), ...patch };
    update(i, { condition: cond });
  };
  const remove = (i) => onChange(branches.filter((_, j) => j !== i));
  const addBranch = () => onChange([...branches, { label: `case_${branches.length + 1}`, condition: { field: "", operator: "EQ", value: "" } }]);
  const addElse = () => onChange([...branches, { label: "else", condition: null }]);
  const hasElse = branches.some((b) => b.condition === null);

  return (
    <div className="space-y-2" data-testid="branch-editor">
      <div className="text-xs text-slate-500">
        Each branch fires the first matching condition. Drop edges from this node and label the edge handle to match a branch label.
      </div>
      {branches.map((b, i) => (
        <div key={i} className="border rounded p-2 bg-slate-50 dark:bg-slate-800 space-y-1.5" data-testid={`branch-row-${i}`}>
          <div className="flex items-center gap-1.5">
            <Input
              placeholder="Branch label (e.g. high_value)"
              value={b.label || ""}
              onChange={(e) => update(i, { label: e.target.value })}
              className="text-xs h-7"
              data-testid={`branch-label-${i}`}
            />
            <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700" data-testid={`branch-remove-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
          {b.condition === null ? (
            <div className="text-[11px] text-violet-600 font-medium px-1">Catch-all (else) — matches when no other branch hits.</div>
          ) : (
            <div className="grid grid-cols-3 gap-1">
              <Input
                placeholder="field"
                value={b.condition?.field || ""}
                onChange={(e) => updateCond(i, { field: e.target.value })}
                className="text-xs h-7"
                data-testid={`branch-field-${i}`}
              />
              <Select value={b.condition?.operator || "EQ"} onValueChange={(v) => updateCond(i, { operator: v })}>
                <SelectTrigger className="text-xs h-7" data-testid={`branch-op-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["EQ", "NEQ", "GT", "LT", "GTE", "LTE", "CONTAINS", "IS_NULL", "IS_NOT_NULL"].map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="value"
                value={b.condition?.value ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const num = Number(raw);
                  updateCond(i, { value: raw !== "" && !isNaN(num) ? num : raw });
                }}
                className="text-xs h-7"
                data-testid={`branch-value-${i}`}
              />
            </div>
          )}
        </div>
      ))}
      <div className="flex gap-1.5">
        <Button type="button" size="sm" variant="outline" onClick={addBranch} data-testid="branch-add-btn" className="text-xs h-7">
          <Plus size={12} className="mr-1" /> Add branch
        </Button>
        {!hasElse && (
          <Button type="button" size="sm" variant="outline" onClick={addElse} data-testid="branch-add-else-btn" className="text-xs h-7">
            + Else
          </Button>
        )}
      </div>
    </div>
  );
}

export { FlowCanvas, NODE_STYLE };
